import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = '/stadiums/';

const StadiumForm: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    city: '',
    country: '',
    capacity: '',
    is_international: false,
  });

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      axios.get(API_URL + id + '/').then(res => {
        setForm({
          name: res.data.name || '',
          city: res.data.city || '',
          country: res.data.country || '',
          capacity: res.data.capacity || '',
          is_international: !!res.data.is_international,
        });
      }).catch(() => setError('Failed to load stadium')).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = { ...form, capacity: form.capacity ? Number(form.capacity) : null };
      if (isEdit) {
        await axios.put(API_URL + id + '/', payload);
        setSuccess('Stadium updated successfully');
      } else {
        await axios.post(API_URL, payload);
        setSuccess('Stadium created successfully');
        setForm({ name: '', city: '', country: '', capacity: '', is_international: false });
      }
      setTimeout(() => navigate('/football/stadiums'), 1000);
    } catch (err: any) {
      setError('Failed to save stadium');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxWidth={500} mx="auto" mt={4}>
      <Typography variant="h5" mb={2}>{isEdit ? 'Edit' : 'Create'} Football Stadium</Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
          <TextField label="City" name="city" value={form.city} onChange={handleChange} fullWidth />
          <TextField label="Country" name="country" value={form.country} onChange={handleChange} fullWidth />
          <TextField label="Capacity" name="capacity" value={form.capacity} onChange={handleChange} type="number" fullWidth />
          <FormControlLabel control={<Checkbox checked={form.is_international} onChange={handleChange} name="is_international" />} label="International" />
          {error && <Typography color="error">{error}</Typography>}
          {success && <Typography color="success.main">{success}</Typography>}
          <Button type="submit" variant="contained" disabled={loading}>{isEdit ? 'Update' : 'Create'}</Button>
        </Stack>
      </form>
    </Box>
  );
};

export default StadiumForm; 